import { Outlet } from "react-router";

import { EmptystateLockColored } from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import EmptyState from "ds/components/EmptyState";
import useHasSpaceAdminAccess from "shared/Space/useHasSpaceAdminAccess";
import useHasSpaceReadAccess from "shared/Space/useHasSpaceReadAccess";

type SpaceDetailsAccessGateLevel = "read" | "admin";

type SpaceDetailsAccessGateProps = {
  spaceId: string;
  requiredAccess: SpaceDetailsAccessGateLevel;
};

const EMPTY_STATE_CONTENT: Record<SpaceDetailsAccessGateLevel, { title: string; caption: string }> =
  {
    read: {
      title: "You don't have access to this space",
      caption: "Reach out to an account admin to request access.",
    },
    admin: {
      title: "Admin access required",
      caption: "Only admins of this space can see who has access to it.",
    },
  };

// Layout route guarding the space details tabs. Their queries are authorized per space (read for
// Stacks/Contexts/Policies, admin for the access tables); below the required level the tab could
// only error, so instead of mounting it (and flashing every failed operation) the gate explains
// why the content is unavailable.
const SpaceDetailsAccessGate = ({ spaceId, requiredAccess }: SpaceDetailsAccessGateProps) => {
  const hasSpaceAdminAccess = useHasSpaceAdminAccess(spaceId);
  const hasSpaceReadAccess = useHasSpaceReadAccess(spaceId);

  const hasRequiredAccess = requiredAccess === "admin" ? hasSpaceAdminAccess : hasSpaceReadAccess;

  if (hasRequiredAccess) {
    return <Outlet />;
  }

  const emptyState = EMPTY_STATE_CONTENT[requiredAccess];

  return (
    <ListContentWrapper>
      <EmptyState
        title={emptyState.title}
        icon={EmptystateLockColored}
        caption={emptyState.caption}
      />
    </ListContentWrapper>
  );
};

export default SpaceDetailsAccessGate;
